import { useEffect, useRef, useState } from "react";
import styles from "./BankCards.module.scss";
import BankCardHeader from "../../../components/pages/admin-panels/bank-cards/bank-card-header/BankCardHeader";
import BankCardList from "../../../components/pages/admin-panels/bank-cards/bank-card-list/BankCardList";
import DeleteCardModal from "../../../components/pages/admin-panels/bank-cards/delete-card-modal/DeleteCardModal";
import { BankCardsServices } from "../../../services/bankCards.service";
import Spinner from "../../../components/shared/spinner/Spinner";
import TitleUpdater from "../../../components/shared/title/TitleUpdater";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { fetchTranslation } from "../../../redux/Slices/translationDataSlice";

export interface BankCard {
  id: number;
  pan_masked: string;
  card_type: string;
  is_default: boolean;
}

export interface BankCardsPageData {
  title?: string;
  add_card_btn?: string;
  card_number_label?: string;
  delete_btn?: string;
  confirm_delete?: string;
  cancel_btn?: string;
  no_cards_text?: string;
}

export default function BankCards() {
  const language = useSelector((state: RootState) => state.language.language);
  const { data } = useSelector((state: RootState) => state.translationData);
  const dispatch = useDispatch<AppDispatch>();

  const [loading, setLoading] = useState(false);
  const [addCardLoading, setAddCardLoading] = useState(false);
  const [cards, setCards] = useState<BankCard[]>([]);
  const [pageData, setPageData] = useState<BankCardsPageData>({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedCardId, setSelectedCardId] = useState<number | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const initialRender = useRef(true);

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      if (!data) {
        dispatch(fetchTranslation());
      }
    } else {
      dispatch(fetchTranslation());
    }
  }, [dispatch, language]);

  useEffect(() => {
    fetchCards();
  }, [language, refreshKey]);

  const fetchCards = async () => {
    setLoading(true);
    try {
      const response = await BankCardsServices.getBankCards();
      setCards(response.data?.data || []);
    } catch (error) {
      console.error("Error fetching cards:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddCard = async () => {
    setAddCardLoading(true);
    try {
      const response = await BankCardsServices.addBankCard();
      const paymentUrl = response.data?.original?.content || response.data?.url;
      if (paymentUrl) {
        window.location.href = paymentUrl;
      }
    } catch (error) {
      console.error("Error adding card:", error);
    } finally {
      setAddCardLoading(false);
    }
  };

  const handleDeleteCard = (cardId: number) => {
    setSelectedCardId(cardId);
    setIsDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (selectedCardId) {
      try {
        await BankCardsServices.deleteBankCard(selectedCardId);
        setRefreshKey((prev) => prev + 1);
        setIsDeleteModalOpen(false);
        setSelectedCardId(null);
      } catch (error) {
        console.error("Error deleting card:", error);
      }
    }
  };

  return (
    <div className={styles.bank_cards_container}>
      <TitleUpdater
        title={
          data?.colibri && data?.auth_bank_cards
            ? `${data?.colibri} | ${data?.auth_bank_cards}`
            : "Colibri"
        }
      />

      {loading ? (
        <Spinner size="large" />
      ) : (
        <>
          <BankCardHeader
            pageData={pageData}
            onAddCard={handleAddCard}
            isLoading={addCardLoading}
            translationData={data}
          />

          <BankCardList
            cards={cards}
            pageData={pageData}
            onDeleteCard={handleDeleteCard}
            translationData={data}
          />

          <DeleteCardModal
            isOpen={isDeleteModalOpen}
            onClose={() => {
              setIsDeleteModalOpen(false);
              setSelectedCardId(null);
            }}
            onConfirm={handleDeleteConfirm}
            pageData={pageData}
            translationData={data}
          />
        </>
      )}
    </div>
  );
}
